import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { MorphSVGPlugin } from 'gsap/MorphSVGPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  getCompanionPresetConfig,
  type CompanionPresetConfig,
} from '@/config/companionPresets';
import { resolveCompanionPresetConfig } from '@/utils/companionPresetAttrs';
import styles from './BackgroundBubble.module.css';
import {
  clearMorphTimelineCache,
  createMorphTimelineCache,
} from './backgroundBubbleMorph';
import {
  COMPANION_CONFIG,
  IDLE_SIZE,
  SHAPES,
  collectPlatforms,
  computeNeighborInfluence,
  getPlatformCenterX,
  getPlatformCenterY,
  getShapeName,
  getElementRotationDeg,
  getViewportCenter,
  lerp,
  pickBestPlatform,
  type NeighborInfluence,
  type PlatformTarget,
  type ScrollBlendState,
} from './backgroundBubbleUtils';

gsap.registerPlugin(MorphSVGPlugin, ScrollTrigger);

const SCRUB_AMOUNT = 0.72;
const DEFOCUS_SIZE_CAP_RATIO = 0.42;
const DEFOCUS_SIZE_CAP_PX = 360;
const REBUILD_DEBOUNCE_MS = 120;
const ENDPOINT_EPSILON = 0.08;
const MAIN_SQUEEZE_FACTOR = 0.08;
const QUICK_POSITION_DURATION = 0.9;
const QUICK_SIZE_DURATION = 0.68;
/** Durée du morph de retour vers idle (sortie d'attraction). */
const SETTLE_MORPH_DURATION = 1.75;
const SETTLE_STRETCH_RATIO = 0.42;
/** Voyage vers le blob : rétrécit vite puis reste petit jusqu'à l'arrivée réelle. */
const SUCTION_SHRINK_DURATION = 0.4;
const ARRIVAL_EXPAND_DURATION = 0.32;
const ARRIVAL_DISTANCE_PX = 36;
const SUCTION_SIZE_RATIO = 0.08;
const SUCTION_SLIM_SCALE_X = 0.1;
const SUCTION_SLIM_SCALE_Y = 1.75;
/** Morph légèrement plus grand que le blob actif pour déborder derrière sa forme. */
/** Fallback ; remplacé dynamiquement via preset Strapi. */
const DEFAULT_PLATFORM_MORPH_SCALE = 1.1;
const PLATFORM_LOCK_DURATION = 0.36;
const UNEAT_DURATION = 0.55;
/** Délai après lock avant stabilisation sur la plateforme (morph déjà sur le blob). */
const EAT_AFTER_LOCK_DELAY = 0.08;
/** Délai avant de repartir vers le prochain blob. */
const TRAVEL_AFTER_UNEAT_DELAY = 0.3;
/** Anticipation squash → pause → stretch avant voyage. */
const LEAN_ROTATION_FACTOR = 0.06;

type ApproachPayload = {
  targetPath: string;
  targetWidth: number;
  targetHeight: number;
  targetRotation: number;
  onDone?: () => void;
};

type IdleMorphStep = {
  shape: SHAPES;
  duration: number;
  ease: string;
  pauseAfter?: number;
};

const IDLE_MORPH_SEQUENCES: IdleMorphStep[][] = [
  [
    { shape: SHAPES.PUFFY, duration: 1.1, ease: 'sine.inOut', pauseAfter: 0.06 },
    { shape: SHAPES.SQUEEZING, duration: 1.0, ease: 'power2.inOut', pauseAfter: 0.05 },
    { shape: SHAPES.WOBBLE, duration: 0.95, ease: 'sine.out', pauseAfter: 0.06 },
    { shape: SHAPES.DRIPPING, duration: 1.1, ease: 'power1.out', pauseAfter: 0.05 },
    { shape: SHAPES.REBOUND, duration: 0.95, ease: 'power2.out', pauseAfter: 0.06 },
    { shape: SHAPES.SETTLED, duration: 1.0, ease: 'sine.inOut', pauseAfter: 0.05 },
    { shape: SHAPES.SWELL, duration: 1.1, ease: 'power1.inOut', pauseAfter: 0.06 },
    { shape: SHAPES.IDLE, duration: 1.3, ease: 'sine.inOut' },
  ],
  [
    { shape: SHAPES.SWELL, duration: 0.95, ease: 'sine.out', pauseAfter: 0.05 },
    { shape: SHAPES.SETTLED, duration: 1.2, ease: 'power2.inOut', pauseAfter: 0.08 },
    { shape: SHAPES.PUFFY, duration: 1.0, ease: 'sine.inOut', pauseAfter: 0.05 },
    { shape: SHAPES.REBOUND, duration: 0.95, ease: 'power2.out', pauseAfter: 0.06 },
    { shape: SHAPES.IDLE, duration: 1.3, ease: 'sine.inOut', pauseAfter: 0.08 },
    { shape: SHAPES.WOBBLE, duration: 1.0, ease: 'power3.out', pauseAfter: 0.05 },
    { shape: SHAPES.DRIPPING, duration: 1.1, ease: 'sine.out', pauseAfter: 0.06 },
    { shape: SHAPES.IDLE, duration: 1.2, ease: 'power2.out' },
  ],
  [
    { shape: SHAPES.PUFFY, duration: 0.85, ease: 'power3.out', pauseAfter: 0.04 },
    { shape: SHAPES.SQUEEZING, duration: 0.8, ease: 'power2.in', pauseAfter: 0.04 },
    { shape: SHAPES.WOBBLE, duration: 0.85, ease: 'sine.inOut', pauseAfter: 0.05 },
    { shape: SHAPES.IDLE, duration: 1.0, ease: 'sine.inOut', pauseAfter: 0.06 },
    { shape: SHAPES.REBOUND, duration: 0.9, ease: 'power2.out', pauseAfter: 0.04 },
    { shape: SHAPES.DRIPPING, duration: 0.95, ease: 'sine.out', pauseAfter: 0.05 },
    { shape: SHAPES.SETTLED, duration: 1.0, ease: 'power2.inOut', pauseAfter: 0.06 },
    { shape: SHAPES.SWELL, duration: 0.95, ease: 'sine.out', pauseAfter: 0.04 },
    { shape: SHAPES.IDLE, duration: 1.1, ease: 'power2.out' },
  ],
];

function pickIdleMorphSequence(): IdleMorphStep[] {
  return IDLE_MORPH_SEQUENCES[Math.floor(Math.random() * IDLE_MORPH_SEQUENCES.length)];
}

function companionWobbleScale(
  t: number,
  cfg: typeof COMPANION_CONFIG[number],
): number {
  return 1
    + Math.sin(t * cfg.wobbleFreq + cfg.wobblePhase) * 0.08
    + Math.sin(t * cfg.wobbleFreq * cfg.wobbleHarmonic + cfg.wobblePhase * 1.3) * 0.03;
}

type CompanionState = {
  x: number;
  y: number;
  size: number;
};

type BlendPose = {
  cx: number;
  cy: number;
  width: number;
  height: number;
  fromPath: string;
  toPath: string;
  progress: number;
  isIdle: boolean;
  exclude: HTMLElement[];
};

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Attiré par un blob → lock total (scroll n'influence plus forme/position).
 * Sinon → blend scroll entre plateformes / idle.
 */
function resolveTrackingState(): ScrollBlendState {
  const nearest = pickBestPlatform(null);

  if (nearest) {
    return {
      from: nearest,
      to: null,
      progress: 0,
      isIdle: false,
    };
  }

  return { from: null, to: null, progress: 0, isIdle: true };
}

function BackgroundBubble() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const blobRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const blobContainerRef = useRef<HTMLDivElement>(null);
  const companionRefs = useRef<(SVGSVGElement | null)[]>([]);

  const companionStateRef = useRef<CompanionState[]>(
    COMPANION_CONFIG.map(() => ({ x: 0, y: 0, size: 0 })),
  );
  const activePresetRef = useRef<CompanionPresetConfig>(
    getCompanionPresetConfig('standard'),
  );
  const blendPoseRef = useRef<BlendPose>({
    cx: 0,
    cy: 0,
    width: 420,
    height: 420,
    fromPath: SHAPES.IDLE,
    toPath: SHAPES.IDLE,
    progress: 0,
    isIdle: true,
    exclude: [],
  });
  const influenceRef = useRef<NeighborInfluence>({ repulseX: 0, repulseY: 0, squeeze: 0 });

  useEffect(() => {
    if (!blobRef.current || !pathRef.current || !blobContainerRef.current) return undefined;

    const reducedMotion = prefersReducedMotion();
    const morphCache = createMorphTimelineCache();
    let scrollTrigger: ScrollTrigger | null = null;
    let rebuildTimer: ReturnType<typeof setTimeout> | null = null;
    let followRafId = 0;
    let firstPositionSet = false;
    let platformsSnapshot: PlatformTarget[] = [];
    let lockedElement: HTMLElement | null = null;
    let eatenElement: HTMLElement | null = null;
    let settleTl: gsap.core.Timeline | null = null;
    let eatTl: gsap.core.Timeline | null = null;
    let isSettling = false;
    let isEaten = false;
    let isSneaking = false;
    let isApproaching = false;
    let approachExpanding = false;
    let approachPayload: ApproachPayload | null = null;
    let lockedRotationDeg = 0;
    const pendingCalls: gsap.core.Tween[] = [];

    function clearPendingCalls() {
      pendingCalls.forEach((call) => call.kill());
      pendingCalls.length = 0;
    }

    function scheduleCall(delay: number, fn: () => void) {
      const call = gsap.delayedCall(delay, fn);
      pendingCalls.push(call);
      return call;
    }
    let quickTop = gsap.quickTo(blobRef.current, 'top', { duration: QUICK_POSITION_DURATION, ease: 'power2.out' });
    let quickLeft = gsap.quickTo(blobRef.current, 'left', { duration: QUICK_POSITION_DURATION, ease: 'power2.out' });
    let quickWidth = gsap.quickTo(blobRef.current, 'width', { duration: QUICK_SIZE_DURATION, ease: 'power2.out' });
    let quickHeight = gsap.quickTo(blobRef.current, 'height', { duration: QUICK_SIZE_DURATION, ease: 'power2.out' });
    const idleBoot = (() => {
      const containerRect = blobContainerRef.current!.getBoundingClientRect();
      const { x: centerX, y: centerY } = getViewportCenter();
      return {
        cx: centerX - containerRect.left,
        cy: centerY - containerRect.top,
      };
    })();

    gsap.set(blobRef.current, {
      opacity: 0,
      xPercent: -50,
      yPercent: -50,
      x: 0,
      y: 0,
      top: idleBoot.cy,
      left: idleBoot.cx,
      width: IDLE_SIZE,
      height: IDLE_SIZE,
    });
    gsap.set(pathRef.current, { attr: { d: SHAPES.IDLE } });
    blendPoseRef.current = {
      ...blendPoseRef.current,
      cx: idleBoot.cx,
      cy: idleBoot.cy,
      isIdle: true,
    };

    companionRefs.current.forEach((companion, i) => {
      if (!companion) return;
      gsap.set(companion, {
        opacity: COMPANION_CONFIG[i].opacity,
        xPercent: -50,
        yPercent: -50,
      });
    });

    const driftTl = gsap.timeline({ repeat: -1, paused: reducedMotion });
    if (!reducedMotion) {
      driftTl
        .to(blobRef.current, {
          xPercent: -47,
          yPercent: -53,
          scaleX: 1.04,
          scaleY: 1.08,
          rotation: 5,
          duration: 2.5,
          ease: 'power2.inOut',
        })
        .to({}, { duration: 0.45 })
        .to(blobRef.current, {
          xPercent: -54,
          yPercent: -46,
          scaleX: 0.96,
          scaleY: 0.94,
          rotation: -4,
          duration: 1.4,
          ease: 'sine.out',
        })
        .to({}, { duration: 0.3 })
        .to(blobRef.current, {
          xPercent: -49,
          yPercent: -52,
          scaleX: 1.03,
          scaleY: 1.05,
          rotation: 7,
          duration: 4.6,
          ease: 'power1.inOut',
        })
        .to({}, { duration: 0.55 })
        .to(blobRef.current, {
          xPercent: -52,
          yPercent: -48,
          scaleX: 0.98,
          scaleY: 0.97,
          rotation: -6,
          duration: 3.2,
          ease: 'sine.inOut',
        })
        .to({}, { duration: 0.4 })
        .to(blobRef.current, {
          xPercent: -50,
          yPercent: -50,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
          duration: 4.4,
          ease: 'power2.out',
        });
    }

    function buildIdleMorphTimeline(sequence: IdleMorphStep[]): gsap.core.Timeline {
      const tl = gsap.timeline({ repeat: -1, paused: true });
      if (!pathRef.current || reducedMotion) return tl;

      sequence.forEach((step) => {
        tl.to(pathRef.current, {
          morphSVG: step.shape,
          duration: step.duration,
          ease: step.ease,
        });
        if (step.pauseAfter) {
          tl.to({}, { duration: step.pauseAfter });
        }
      });

      return tl;
    }

    let idleMorphTl = buildIdleMorphTimeline(pickIdleMorphSequence());

    let platformMorphScale = DEFAULT_PLATFORM_MORPH_SCALE;
    let platformWobbleTl = gsap.timeline({ repeat: -1, paused: true });

    function buildPlatformWobble() {
      platformWobbleTl.kill();
      platformWobbleTl = gsap.timeline({ repeat: -1, paused: true });
      if (reducedMotion || !blobRef.current) return;
      platformWobbleTl
        .to(blobRef.current, {
          scaleX: platformMorphScale * 1.04,
          scaleY: platformMorphScale * 0.98,
          duration: 2.7,
          ease: 'power2.inOut',
        })
        .to({}, { duration: 0.35 })
        .to(blobRef.current, {
          scaleX: platformMorphScale * 0.97,
          scaleY: platformMorphScale * 1.02,
          duration: 1.9,
          ease: 'sine.out',
        })
        .to({}, { duration: 0.5 })
        .to(blobRef.current, {
          scaleX: platformMorphScale * 1.01,
          scaleY: platformMorphScale * 0.99,
          duration: 3.1,
          ease: 'power1.inOut',
        });
    }

    function companionShapePath(config: CompanionPresetConfig): string {
      return config.companionShape === 'puffy' ? SHAPES.PUFFY : SHAPES.IDLE;
    }

    function applyCompanionPreset(config: CompanionPresetConfig) {
      activePresetRef.current = config;
      platformMorphScale = config.platformScale;
      const pathD = companionShapePath(config);
      companionRefs.current.forEach((companion) => {
        const path = companion?.querySelector('path');
        if (path) path.setAttribute('d', pathD);
      });
      const wasPlaying = !platformWobbleTl.paused();
      buildPlatformWobble();
      if (wasPlaying) platformWobbleTl.restart().resume();
    }

    function syncPresetFromElement(element: HTMLElement | null) {
      applyCompanionPreset(
        element
          ? resolveCompanionPresetConfig(element)
          : getCompanionPresetConfig('standard'),
      );
    }

    buildPlatformWobble();

    function restartIdleMorph() {
      idleMorphTl.kill();
      idleMorphTl = buildIdleMorphTimeline(pickIdleMorphSequence());
      if (pathRef.current) gsap.set(pathRef.current, { morphSVG: SHAPES.IDLE });
      idleMorphTl.restart().resume();
    }

    function setMorphState(state: 'idle' | 'platform' | 'traveling') {
      wrapperRef.current?.setAttribute('data-morph-state', state);
    }

    function getDefocusSizePx(): { width: number; height: number } {
      const current = blobRef.current?.getBoundingClientRect();
      const cap = Math.min(DEFOCUS_SIZE_CAP_PX, window.innerWidth * DEFOCUS_SIZE_CAP_RATIO);
      if (!current) {
        const fallback = Math.min(420, window.innerWidth * 0.52);
        return { width: fallback, height: fallback };
      }
      return {
        width: Math.min(current.width || 420, cap),
        height: Math.min(current.height || 420, cap),
      };
    }

    function getIdleCenter(): { cx: number; cy: number } {
      const containerRect = blobContainerRef.current!.getBoundingClientRect();
      const { x: centerX, y: centerY } = getViewportCenter();
      return {
        cx: centerX - containerRect.left,
        cy: centerY - containerRect.top,
      };
    }

    function resolvePoseFromBlend(
      from: PlatformTarget | null,
      to: PlatformTarget | null,
      progress: number,
      isIdle: boolean,
    ): BlendPose {
      const containerRect = blobContainerRef.current!.getBoundingClientRect();
      const idle = getIdleCenter();
      const defocus = getDefocusSizePx();
      const exclude: HTMLElement[] = [];
      if (from) exclude.push(from.element);
      if (to) exclude.push(to.element);

      if (isIdle || (!from && !to)) {
        return {
          cx: idle.cx,
          cy: idle.cy,
          width: defocus.width,
          height: defocus.height,
          fromPath: SHAPES.IDLE,
          toPath: SHAPES.IDLE,
          progress: 0,
          isIdle: true,
          exclude,
        };
      }

      if (from && !to) {
        const fromCx = getPlatformCenterX(from) - containerRect.left;
        const fromCy = getPlatformCenterY(from) - containerRect.top;
        return {
          cx: lerp(fromCx, idle.cx, progress),
          cy: lerp(fromCy, idle.cy, progress),
          width: lerp(from.rect.width, defocus.width, progress),
          height: lerp(from.rect.height, defocus.height, progress),
          fromPath: from.shapePath,
          toPath: SHAPES.IDLE,
          progress,
          isIdle: false,
          exclude,
        };
      }

      if (!from && to) {
        const toCx = getPlatformCenterX(to) - containerRect.left;
        const toCy = getPlatformCenterY(to) - containerRect.top;
        return {
          cx: lerp(idle.cx, toCx, progress),
          cy: lerp(idle.cy, toCy, progress),
          width: lerp(defocus.width, to.rect.width, progress),
          height: lerp(defocus.height, to.rect.height, progress),
          fromPath: SHAPES.IDLE,
          toPath: to.shapePath,
          progress,
          isIdle: false,
          exclude,
        };
      }

      const fromCx = getPlatformCenterX(from!) - containerRect.left;
      const fromCy = getPlatformCenterY(from!) - containerRect.top;
      const toCx = getPlatformCenterX(to!) - containerRect.left;
      const toCy = getPlatformCenterY(to!) - containerRect.top;

      return {
        cx: lerp(fromCx, toCx, progress),
        cy: lerp(fromCy, toCy, progress),
        width: lerp(from!.rect.width, to!.rect.width, progress),
        height: lerp(from!.rect.height, to!.rect.height, progress),
        fromPath: from!.shapePath,
        toPath: to!.shapePath,
        progress,
        isIdle: false,
        exclude,
      };
    }

    function killSettleMorph() {
      settleTl?.kill();
      settleTl = null;
      isSettling = false;
      isSneaking = false;
      isApproaching = false;
      approachExpanding = false;
      approachPayload = null;
    }

    function killEatEffect() {
      eatTl?.kill();
      eatTl = null;
    }

    /**
     * Lock sur plateforme : le morph reste visible derrière le blob actif
     * (légèrement plus grand pour déborder), sans grossir ni décaler les blobs.
     */
    function playEatEffect(target: HTMLElement) {
      if (!blobRef.current) return;

      killEatEffect();
      eatenElement = target;
      isEaten = true;
      lockedRotationDeg = getElementRotationDeg(target);

      driftTl.pause();

      syncPresetFromElement(target);

      if (reducedMotion) {
        gsap.set(blobRef.current, {
          opacity: 1,
          scale: platformMorphScale,
          scaleX: platformMorphScale,
          scaleY: platformMorphScale,
          rotation: lockedRotationDeg,
          xPercent: -50,
          yPercent: -50,
        });
        return;
      }

      eatTl = gsap.timeline({ defaults: { overwrite: 'auto' } });
      eatTl.to(blobRef.current, {
        scaleX: platformMorphScale,
        scaleY: platformMorphScale,
        rotation: lockedRotationDeg,
        xPercent: -50,
        yPercent: -50,
        opacity: 1,
        duration: PLATFORM_LOCK_DURATION,
        ease: 'back.out(1.3)',
      }, 0);
    }

    /** Sortie : morph reprend son échelle normale. */
    function playUneatEffect(fast = false) {
      if (!blobRef.current) return;

      killEatEffect();
      eatenElement = null;
      isEaten = false;

      const duration = fast ? UNEAT_DURATION * 0.65 : UNEAT_DURATION;

      if (reducedMotion) {
        gsap.set(blobRef.current, { opacity: 1, scale: 1, scaleX: 1, scaleY: 1 });
        return;
      }

      eatTl = gsap.timeline({ defaults: { overwrite: 'auto' } });
      eatTl.to(blobRef.current, {
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        opacity: 1,
        duration,
        ease: 'power2.out',
      }, 0);
    }

    /** Retour vers la forme idle (sortie d'attraction). */
    function playSettleMorph(targetPath: string, onDone?: () => void) {
      if (!pathRef.current || reducedMotion) {
        if (pathRef.current) gsap.set(pathRef.current, { morphSVG: targetPath });
        onDone?.();
        return;
      }

      killSettleMorph();
      idleMorphTl.pause();
      driftTl.pause();
      isSettling = true;
      setMorphState('traveling');

      const stretch = SETTLE_MORPH_DURATION * SETTLE_STRETCH_RATIO;
      const settle = SETTLE_MORPH_DURATION * (1 - SETTLE_STRETCH_RATIO);

      settleTl = gsap.timeline({
        onComplete: () => {
          isSettling = false;
          settleTl = null;
          if (pathRef.current) gsap.set(pathRef.current, { attr: { d: targetPath } });
          onDone?.();
        },
      })
        .to(pathRef.current, {
          morphSVG: SHAPES.TRANSITION,
          duration: stretch,
          ease: 'sine.inOut',
        }, 0)
        .to(pathRef.current, {
          morphSVG: targetPath,
          duration: settle,
          ease: 'power2.out',
        }, stretch);
    }

    /** Phase 1 : rétrécit vite et reste fin pendant tout le trajet vers le blob. */
    function startSuctionTravel(
      targetPath: string,
      targetWidth: number,
      targetHeight: number,
      targetRotation: number,
      onDone?: () => void,
    ) {
      if (!pathRef.current || !blobRef.current || reducedMotion) {
        if (pathRef.current) gsap.set(pathRef.current, { morphSVG: targetPath });
        if (blobRef.current) {
          gsap.set(blobRef.current, {
            width: targetWidth,
            height: targetHeight,
            scale: 1,
            scaleX: 1,
            scaleY: 1,
            rotation: targetRotation,
            xPercent: -50,
            yPercent: -50,
          });
        }
        onDone?.();
        return;
      }

      killSettleMorph();
      idleMorphTl.pause();
      driftTl.pause();

      approachPayload = {
        targetPath,
        targetWidth,
        targetHeight,
        targetRotation,
        onDone,
      };
      isApproaching = true;
      isSneaking = true;
      isSettling = true;
      approachExpanding = false;
      lockedRotationDeg = targetRotation;
      setMorphState('traveling');
      wrapperRef.current?.setAttribute('data-travel-mode', 'suction');

      gsap.killTweensOf(blobRef.current, 'width,height,scale,scaleX,scaleY,rotation,xPercent,yPercent');

      const startW = (gsap.getProperty(blobRef.current, 'width') as number) || blendPoseRef.current.width;
      const startH = (gsap.getProperty(blobRef.current, 'height') as number) || blendPoseRef.current.height;
      const suctionW = startW * SUCTION_SIZE_RATIO;
      const suctionH = startH * SUCTION_SIZE_RATIO;

      gsap.set(blobRef.current, {
        scale: 1,
        scaleX: 1,
        scaleY: 1,
        rotation: targetRotation,
        xPercent: -50,
        yPercent: -50,
      });

      settleTl = gsap.timeline({
        onComplete: () => {
          settleTl = null;
        },
      });

      settleTl
        .to(pathRef.current, {
          morphSVG: SHAPES.TRANSITION,
          duration: SUCTION_SHRINK_DURATION,
          ease: 'power4.in',
        }, 0)
        .to(blobRef.current, {
          width: suctionW,
          height: suctionH,
          scaleX: SUCTION_SLIM_SCALE_X,
          scaleY: SUCTION_SLIM_SCALE_Y,
          rotation: targetRotation,
          xPercent: -50,
          yPercent: -50,
          duration: SUCTION_SHRINK_DURATION,
          ease: 'power4.in',
        }, 0);
    }

    /** Phase 2 : arrivée derrière le blob — reprend taille + forme. */
    function playArrivalExpand() {
      if (!approachPayload || approachExpanding || !blobRef.current || !pathRef.current) return;

      const {
        targetPath,
        targetWidth,
        targetHeight,
        targetRotation,
        onDone,
      } = approachPayload;

      approachExpanding = true;
      isApproaching = false;
      isSneaking = false;

      settleTl?.kill();
      settleTl = null;

      settleTl = gsap.timeline({
        onComplete: () => {
          isSettling = false;
          approachExpanding = false;
          approachPayload = null;
          settleTl = null;
          wrapperRef.current?.removeAttribute('data-travel-mode');
          if (pathRef.current) gsap.set(pathRef.current, { attr: { d: targetPath } });
          gsap.set(blobRef.current, {
            width: targetWidth,
            height: targetHeight,
            scale: 1,
            scaleX: 1,
            scaleY: 1,
            rotation: targetRotation,
            xPercent: -50,
            yPercent: -50,
          });
          onDone?.();
        },
      });

      settleTl
        .to(blobRef.current, {
          width: targetWidth,
          height: targetHeight,
          scaleX: 1,
          scaleY: 1,
          rotation: targetRotation,
          xPercent: -50,
          yPercent: -50,
          duration: ARRIVAL_EXPAND_DURATION,
          ease: 'power4.out',
        })
        .to(pathRef.current, {
          morphSVG: targetPath,
          duration: ARRIVAL_EXPAND_DURATION,
          ease: 'power4.out',
        }, `<${ARRIVAL_EXPAND_DURATION}`);
    }

    function checkApproachArrival(targetCx: number, targetCy: number) {
      if (!isApproaching || approachExpanding || !blobRef.current) return;

      const top = gsap.getProperty(blobRef.current, 'top') as number;
      const left = gsap.getProperty(blobRef.current, 'left') as number;
      const dist = Math.hypot(left - targetCx, top - targetCy);

      if (dist <= ARRIVAL_DISTANCE_PX) {
        playArrivalExpand();
      }
    }

    function approachAndEat(
      target: HTMLElement,
      targetPath: string,
      targetSize?: { width: number; height: number; rotation?: number },
    ) {
      const targetWidth = targetSize?.width ?? blendPoseRef.current.width;
      const targetHeight = targetSize?.height ?? blendPoseRef.current.height;
      const targetRotation = targetSize?.rotation ?? getElementRotationDeg(target);

      syncPresetFromElement(target);
      startSuctionTravel(targetPath, targetWidth, targetHeight, targetRotation, () => {
        scheduleCall(EAT_AFTER_LOCK_DELAY, () => {
          if (lockedElement === target && pickBestPlatform(null)?.element === target) {
            playEatEffect(target);
          }
        });
      });
    }

    function resumeIdleLife(restart = false) {
      if (reducedMotion || !pathRef.current || !blobRef.current) return;
      platformWobbleTl.pause();
      setMorphState('idle');
      wrapperRef.current?.setAttribute('data-shape', 'IDLE');

      if (restart) {
        restartIdleMorph();

        gsap.set(blobRef.current, {
          xPercent: -50,
          yPercent: -50,
          x: 0,
          y: 0,
          scaleX: 1,
          scaleY: 1,
          rotation: 0,
        });
        driftTl.invalidate().restart(true);
      } else {
        if (idleMorphTl.paused()) idleMorphTl.resume();
        if (driftTl.paused()) driftTl.resume();
      }
    }

    /** Annule une approche / eat en cours (reverse scroll) sans pop. */
    function cancelAttraction(animateToIdle: boolean) {
      clearPendingCalls();
      killSettleMorph();
      wrapperRef.current?.removeAttribute('data-travel-mode');
      const hadEat = isEaten || eatenElement !== null;
      lockedElement = null;
      syncPresetFromElement(null);

      if (hadEat) {
        playUneatEffect(true);
      } else if (blobRef.current) {
        // Approche interrompue : remonter opacity/scale depuis l'état courant
        gsap.killTweensOf(blobRef.current, 'opacity,scale,scaleX,scaleY');
        gsap.to(blobRef.current, {
          opacity: 1,
          scale: 1,
          scaleX: 1,
          scaleY: 1,
          duration: UNEAT_DURATION * 0.7,
          ease: 'sine.out',
          overwrite: 'auto',
        });
        companionRefs.current.forEach((c, i) => {
          if (!c) return;
          gsap.killTweensOf(c, 'opacity,scale');
          gsap.to(c, {
            opacity: COMPANION_CONFIG[i].opacity,
            scale: 1,
            duration: UNEAT_DURATION * 0.7,
            ease: 'sine.out',
            overwrite: 'auto',
          });
        });
      }

      if (animateToIdle) {
        wrapperRef.current?.setAttribute('data-shape', 'IDLE');
        playSettleMorph(SHAPES.IDLE, () => {
          if (!pickBestPlatform(null)) {
            resumeIdleLife(true);
          }
        });
      }
    }

    function updatePeripheralAnimations(pose: BlendPose) {
      if (reducedMotion) {
        driftTl.pause();
        idleMorphTl.pause();
        platformWobbleTl.pause();
        return;
      }

      if (isSettling) {
        driftTl.pause();
        idleMorphTl.pause();
        platformWobbleTl.pause();
        setMorphState('traveling');
        return;
      }

      if (isEaten) {
        driftTl.pause();
        idleMorphTl.pause();
        setMorphState('platform');
        if (platformWobbleTl.paused()) platformWobbleTl.restart().resume();
        return;
      }

      if (!lockedElement) {
        platformWobbleTl.pause();
        resumeIdleLife(false);
        return;
      }

      const atEndpoint = pose.progress <= ENDPOINT_EPSILON || pose.progress >= 1 - ENDPOINT_EPSILON;
      if (atEndpoint) {
        setMorphState('platform');
        driftTl.pause();
        idleMorphTl.pause();
        if (platformWobbleTl.paused()) platformWobbleTl.restart().resume();
      } else {
        setMorphState('traveling');
        driftTl.pause();
        idleMorphTl.pause();
        platformWobbleTl.pause();
      }
    }

    function applyBlendState(
      from: PlatformTarget | null,
      to: PlatformTarget | null,
      progress: number,
      isIdle = false,
      immediate = false,
    ) {
      if (!blobRef.current || !pathRef.current) return;

      const pose = resolvePoseFromBlend(from, to, progress, isIdle);
      blendPoseRef.current = pose;

      const attractedLock = !pose.isIdle && progress === 0 && to === null && from !== null;
      const nextLocked = attractedLock ? from!.element : null;

      // Reverse scroll / perte d'attraction : annuler même pendant settle (évite pop)
      if (lockedElement !== null && !attractedLock) {
        cancelAttraction(true);
      } else if (attractedLock && lockedElement !== nextLocked) {
        const switching = lockedElement !== null || isEaten || isSettling;
        clearPendingCalls();

        if (switching) {
          // Interrompre approche/eat en cours, puis repartir vers le nouveau blob
          cancelAttraction(false);
          lockedElement = nextLocked;
          const known = getShapeName(pose.fromPath as SHAPES);
          wrapperRef.current?.setAttribute(
            'data-shape',
            known === 'IDLE' && pose.fromPath !== SHAPES.IDLE ? 'BLOB' : known,
          );
          scheduleCall(TRAVEL_AFTER_UNEAT_DELAY, () => {
            if (lockedElement === nextLocked && nextLocked) {
              approachAndEat(nextLocked, pose.fromPath, {
                width: pose.width,
                height: pose.height,
                rotation: getElementRotationDeg(nextLocked),
              });
            }
          });
        } else {
          lockedElement = nextLocked;
          const known = getShapeName(pose.fromPath as SHAPES);
          wrapperRef.current?.setAttribute(
            'data-shape',
            known === 'IDLE' && pose.fromPath !== SHAPES.IDLE ? 'BLOB' : known,
          );
          approachAndEat(nextLocked!, pose.fromPath, {
            width: pose.width,
            height: pose.height,
            rotation: getElementRotationDeg(nextLocked!),
          });
        }
      }

      const scrollControlsSize = !isSneaking
        && !isSettling
        && !isApproaching
        && !approachExpanding
        && lockedElement === null;

      if (immediate || !firstPositionSet) {
        gsap.set(blobRef.current, {
          top: pose.cy,
          left: pose.cx,
          ...(scrollControlsSize ? { width: pose.width, height: pose.height } : {}),
          ...(isEaten ? {} : { opacity: 1 }),
        });
      } else {
        quickTop(pose.cy);
        quickLeft(pose.cx);
        if (scrollControlsSize) {
          quickWidth(pose.width);
          quickHeight(pose.height);
        }
      }

      if (isApproaching) {
        checkApproachArrival(pose.cx, pose.cy);
      }

      if (attractedLock && lockedElement) {
        const known = getShapeName(
          (pose.progress < 0.5 ? pose.fromPath : pose.toPath) as SHAPES,
        );
        const label = known === 'IDLE' && pose.fromPath !== SHAPES.IDLE ? 'BLOB' : known;
        wrapperRef.current?.setAttribute('data-shape', label);
      } else if (!isSettling && lockedElement === null) {
        wrapperRef.current?.setAttribute('data-shape', 'IDLE');
      }

      updatePeripheralAnimations(pose);

      if (!firstPositionSet) {
        companionStateRef.current.forEach((_, i) => {
          const companionState = companionStateRef.current[i];
          companionState.x = pose.cx + COMPANION_CONFIG[i].offsetX * pose.width;
          companionState.y = pose.cy + COMPANION_CONFIG[i].offsetY * pose.height;
        });
        firstPositionSet = true;
        companionRefs.current.forEach((c) => c && gsap.to(c, { opacity: 0.7, duration: 0.5 }));
      }
    }

    function syncFromScroll(immediate = false) {
      platformsSnapshot = collectPlatforms();

      if (reducedMotion) {
        const best = pickBestPlatform(null);
        if (!best) {
          applyBlendState(null, null, 0, true, immediate);
          return;
        }
        applyBlendState(best, null, 0, false, immediate);
        return;
      }

      const state = resolveTrackingState();
      applyBlendState(state.from, state.to, state.progress, state.isIdle, immediate);
    }

    function killScrollTrigger() {
      scrollTrigger?.kill();
      scrollTrigger = null;
    }

    function rebuildScrollEngine() {
      killScrollTrigger();
      killSettleMorph();
      killEatEffect();
      clearPendingCalls();
      clearMorphTimelineCache(morphCache);
      lockedElement = null;
      eatenElement = null;
      isEaten = false;
      isSneaking = false;
      isApproaching = false;
      approachExpanding = false;
      approachPayload = null;

      // Recreate quickTo after potential killTweens
      if (blobRef.current) {
        gsap.killTweensOf(blobRef.current, 'top,left,width,height');
        quickTop = gsap.quickTo(blobRef.current, 'top', { duration: QUICK_POSITION_DURATION, ease: 'power2.out' });
        quickLeft = gsap.quickTo(blobRef.current, 'left', { duration: QUICK_POSITION_DURATION, ease: 'power2.out' });
        quickWidth = gsap.quickTo(blobRef.current, 'width', { duration: QUICK_SIZE_DURATION, ease: 'power2.out' });
        quickHeight = gsap.quickTo(blobRef.current, 'height', { duration: QUICK_SIZE_DURATION, ease: 'power2.out' });
      }

      syncFromScroll(true);

      scrollTrigger = ScrollTrigger.create({
        start: 0,
        end: 'max',
        scrub: reducedMotion ? false : SCRUB_AMOUNT,
        invalidateOnRefresh: true,
        onUpdate: () => {
          syncFromScroll(false);
        },
        onRefresh: () => {
          syncFromScroll(true);
        },
      });
    }

    function scheduleRebuild() {
      if (rebuildTimer) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        rebuildTimer = null;
        rebuildScrollEngine();
        ScrollTrigger.refresh();
      }, REBUILD_DEBOUNCE_MS);
    }

    function updateCompanions() {
      const pose = blendPoseRef.current;
      const containerRect = blobContainerRef.current?.getBoundingClientRect();
      const containerLeft = containerRect?.left ?? 0;
      const containerTop = containerRect?.top ?? 0;
      const mainRadius = Math.max(pose.width, pose.height) / 2;
      const influence = computeNeighborInfluence(
        pose.cx + containerLeft,
        pose.cy + containerTop,
        mainRadius,
        platformsSnapshot,
        pose.exclude,
      );
      influenceRef.current = influence;

      if (blobRef.current && !reducedMotion && influence.squeeze > 0.05 && !isEaten && !isSettling && !lockedElement) {
        platformWobbleTl.pause();
        const leanRot = Math.atan2(influence.repulseY, influence.repulseX)
          * (180 / Math.PI) * LEAN_ROTATION_FACTOR;
        gsap.set(blobRef.current, {
          scaleX: 1 - influence.squeeze * MAIN_SQUEEZE_FACTOR,
          scaleY: 1 + influence.squeeze * MAIN_SQUEEZE_FACTOR * 0.5,
          rotation: leanRot,
        });
      } else if (blobRef.current && isEaten) {
        gsap.set(blobRef.current, {
          rotation: lockedRotationDeg,
          xPercent: -50,
          yPercent: -50,
        });
      } else if (
        blobRef.current
        && influence.squeeze <= 0.05
        && !pose.isIdle
        && !isEaten
        && !isSettling
      ) {
        const current = gsap.getProperty(blobRef.current, 'scaleX') as number;
        if (Math.abs(current - 1) > 0.001) {
          gsap.set(blobRef.current, { scaleX: 1, scaleY: 1 });
        }
      }

      const t = performance.now() * 0.001;
      const morphRect = isSneaking && blobRef.current
        ? blobRef.current.getBoundingClientRect()
        : null;
      const layoutWidth = morphRect?.width ?? pose.width;
      const layoutHeight = morphRect?.height ?? pose.height;

      const lagMul = 1;
      const preset = activePresetRef.current;
      companionStateRef.current.forEach((_, i) => {
        const cfg = COMPANION_CONFIG[i];
        const companionState = companionStateRef.current[i];
        const targetX = pose.cx + layoutWidth * cfg.offsetX + influence.repulseX * cfg.repulsionWeight;
        const targetY = pose.cy + layoutHeight * cfg.offsetY + influence.repulseY * cfg.repulsionWeight;
        const followLag = Math.min(cfg.lag * lagMul, 1);
        companionState.x += (targetX - companionState.x) * followLag;
        companionState.y += (targetY - companionState.y) * followLag;

        const companion = companionRefs.current[i];
        if (companion) {
          const companionSize = layoutWidth * cfg.scale * preset.scale
            * (1 - influence.squeeze * cfg.squeezeWeight);
          const wobbleScale = reducedMotion ? 1 : companionWobbleScale(t, cfg);
          const companionRot = reducedMotion
            ? 0
            : Math.sin(t * 0.9 + cfg.wobblePhase) * 10
              + Math.sin(t * 1.6 + cfg.wobblePhase * 0.7) * 4;
          gsap.set(companion, {
            top: companionState.y,
            left: companionState.x,
            width: Math.max(8, companionSize * wobbleScale),
            height: Math.max(8, companionSize * wobbleScale),
            rotation: companionRot,
            opacity: preset.showCompanions ? cfg.opacity : 0,
          });
        }
      });

      followRafId = requestAnimationFrame(updateCompanions);
    }

    function handleBubbleAnchorSelect() {
      scheduleRebuild();
    }

    function handleBubbleAnchorSettle() {
      if (rebuildTimer) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(() => {
        rebuildTimer = null;
        rebuildScrollEngine();
        ScrollTrigger.refresh();
      }, 40);
    }

    function handleResize() {
      scheduleRebuild();
    }

    // Defer first build so layout + ScrollSmoother are ready
    const bootRaf = requestAnimationFrame(() => {
      rebuildScrollEngine();
      ScrollTrigger.refresh();
      gsap.to(blobRef.current, { opacity: 1, duration: 0.7, ease: 'power2.out' });
    });
    followRafId = requestAnimationFrame(updateCompanions);

    window.addEventListener('resize', handleResize, { passive: true });
    document.addEventListener('bubble-anchor-select', handleBubbleAnchorSelect);
    document.addEventListener('bubble-anchor-settle', handleBubbleAnchorSettle);

    return () => {
      cancelAnimationFrame(bootRaf);
      cancelAnimationFrame(followRafId);
      if (rebuildTimer) clearTimeout(rebuildTimer);
      killScrollTrigger();
      killSettleMorph();
      killEatEffect();
      clearPendingCalls();
      clearMorphTimelineCache(morphCache);
      driftTl.kill();
      idleMorphTl.kill();
      platformWobbleTl.kill();
      gsap.killTweensOf([blobRef.current, pathRef.current, ...companionRefs.current.filter(Boolean)]);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('bubble-anchor-select', handleBubbleAnchorSelect);
      document.removeEventListener('bubble-anchor-settle', handleBubbleAnchorSettle);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      id="background-bubble"
      data-shape="IDLE"
      data-morph-state="idle"
      className={styles.wrapper}
      aria-hidden="true"
    >
      <div ref={blobContainerRef} className={styles.blobContainer}>
        {COMPANION_CONFIG.map((cfg, i) => (
          <svg
            key={`companion-${cfg.offsetX}-${cfg.offsetY}`}
            ref={(el) => { companionRefs.current[i] = el; }}
            className={styles.companion}
            viewBox="-100 -100 200 200"
            overflow="visible"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.companionPath}
              fill="white"
              d={SHAPES.IDLE}
            />
          </svg>
        ))}
        <svg
          preserveAspectRatio="none"
          ref={blobRef}
          className={styles.blob}
          viewBox="-100 -100 200 200"
          overflow="visible"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path ref={pathRef} id="shape" className={styles.path} fill="white" d={SHAPES.IDLE} />
        </svg>
      </div>
    </div>
  );
}

export default BackgroundBubble;
